import React, { useEffect, useRef, useState } from 'react'

export default function ConnectedDevices ({ me, onChange }) {
  const [status, setStatus] = useState(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const active = useRef(0)
  const inFlight = useRef(false)
  const prior = useRef('')
  const refreshParent = useRef(onChange)
  refreshParent.current = onChange
  const supported = me.supportsDeviceNetwork?.() === true
  const uid = me.self?.uid
  useEffect(() => {
    const generation = ++active.current
    if (!supported || !uid) return
    setStatus(null); setError(''); prior.current = ''
    async function poll () {
      if (inFlight.current) return
      inFlight.current = true
      try {
        const next = await me.deviceNetwork('status')
        if (active.current !== generation || me.self?.uid !== uid) return
        setStatus(next); setError(next.error || '')
        const fingerprint = JSON.stringify([next.devices, next.serving, next.modelPhase, next.credentialRevision])
        if (fingerprint !== prior.current) {
          prior.current = fingerprint
          refreshParent.current?.()
        }
      } catch (failure) {
        if (active.current === generation) setError(failure.message || 'Could not read connected devices.')
      } finally { inFlight.current = false }
    }
    poll()
    const timer = setInterval(poll, 4000)
    return () => { ++active.current; clearInterval(timer) }
  }, [me, uid, supported])

  if (!supported) return null
  async function perform (operation, options) {
    if (busy) return
    const generation = active.current
    setBusy(true); setError('')
    try {
      const next = await me.deviceNetwork(operation, options)
      if (active.current !== generation || me.self?.uid !== uid) return
      setStatus(next); setError(next.error || '')
      await refreshParent.current?.()
    } catch (failure) {
      if (active.current === generation) setError(failure.message || 'Could not update this connection.')
    } finally { if (active.current === generation) setBusy(false) }
  }
  return <section className='subscriptionDevices' aria-label='Connected devices'>
    <div className='subscriptionIntro'>Your devices</div>
    {error && <div className='subscriptionError' role='alert'>{error}</div>}
    <button type='button' className='subscriptionDeviceAction' disabled={busy || !status}
      onClick={() => perform('enable', { enabled: !status.enabled })}>
      Device connections <span>{status?.enabled ? 'ON' : 'OFF'}</span>
    </button>
    {status?.enabled && <>
      <p className='subscriptionProviderInstructions'>Devices signed into the same IntelliKey account connect and sync provider credentials automatically.</p>
      {(status.devices || []).map(device => <div className='subscriptionDevice' key={device.deviceId}>
        <div className='subscriptionDeviceName'>{device.name}<span>{device.online ? 'Online' : 'Offline'}</span></div>
      </div>)}
      {status.devices?.length === 0 && <p className='subscriptionProviderInstructions'>No other devices found yet.</p>}
    </>}
  </section>
}
